import { KontorError, type ErrorCode } from "@kontor/shared/errors";
import { getOrganisationId } from "../../lib/api";
import { supabase } from "../../lib/auth";

/**
 * The event-stream client.
 *
 * This exists instead of `EventSource` for one reason: `EventSource` cannot send
 * headers. Every route in this API is behind a bearer token, so an `EventSource`
 * pointed at the run stream is answered with 401 and the panel never sees a single
 * frame — which is exactly why the assistant had never streamed. `fetch` can carry
 * the Authorization and organisation headers, and its body is a stream, so we parse
 * the wire format ourselves. That is about forty lines and buys back authentication,
 * a real abort signal, and a legible error when the connection is refused.
 *
 * The token is read from Supabase at connect time rather than captured earlier: a
 * long run outlives the access token that started it, and a reconnect must present
 * the refreshed one.
 */

export interface SseRahmen {
  /** The `event:` field. The API emits `ereignis`, `teiltext` and `ende`. */
  art: string;
  daten: unknown;
}

export interface StromOptionen {
  signal: AbortSignal;
  aufRahmen: (rahmen: SseRahmen) => void;
}

/** Resolves when the server closes the stream; rejects on transport or auth failure. */
export async function verfolgeStrom(pfad: string, optionen: StromOptionen): Promise<void> {
  const kopf = new Headers({ Accept: "text/event-stream" });

  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) kopf.set("Authorization", `Bearer ${token}`);

  const organisationId = getOrganisationId();
  if (organisationId) kopf.set("X-Kontor-Organisation", organisationId);

  const antwort = await fetch(pfad, {
    headers: kopf,
    signal: optionen.signal,
    cache: "no-store",
  });

  if (!antwort.ok) throw await alsFehler(antwort);
  if (!antwort.body) {
    throw new KontorError("internal", "Der Server hat keinen Ereignisstrom geliefert.");
  }

  const leser = antwort.body.pipeThrough(new TextDecoderStream()).getReader();
  let puffer = "";

  try {
    for (;;) {
      const { value, done } = await leser.read();
      if (done) break;
      // Normalising CRLF here means the frame splitter below only has one case to
      // handle, whatever proxy sits between the browser and Cloud Run.
      puffer += value.replace(/\r\n/g, "\n");

      for (;;) {
        const grenze = puffer.indexOf("\n\n");
        if (grenze === -1) break;
        const block = puffer.slice(0, grenze);
        puffer = puffer.slice(grenze + 2);
        const rahmen = zerlege(block);
        if (rahmen) optionen.aufRahmen(rahmen);
      }
    }
  } finally {
    // Releasing the lock lets the abort actually tear the socket down instead of
    // leaving a reader pinned to a body nobody reads.
    leser.releaseLock();
  }
}

/**
 * One SSE frame.
 *
 * Returns null for heartbeats (`: puls`) and for anything without a data payload —
 * the server sends a comment every fifteen seconds to keep intermediaries from
 * closing an idle run, and that is not an event.
 */
function zerlege(block: string): SseRahmen | null {
  let art = "message";
  const datenZeilen: string[] = [];

  for (const zeile of block.split("\n")) {
    if (!zeile || zeile.startsWith(":")) continue;
    const trenner = zeile.indexOf(":");
    const feld = trenner === -1 ? zeile : zeile.slice(0, trenner);
    const wert = trenner === -1 ? "" : zeile.slice(trenner + 1).replace(/^ /, "");
    if (feld === "event") art = wert;
    else if (feld === "data") datenZeilen.push(wert);
  }

  if (datenZeilen.length === 0) return null;

  try {
    return { art, daten: JSON.parse(datenZeilen.join("\n")) };
  } catch {
    return null;
  }
}

interface FehlerHuelle {
  error?: { code?: ErrorCode; message?: string };
}

/** Turns a failed response into the same error shape the rest of the app throws. */
async function alsFehler(antwort: Response): Promise<KontorError> {
  if (antwort.status === 401) {
    return new KontorError("unauthenticated", "Die Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
  }

  const istJson = antwort.headers.get("content-type")?.includes("application/json") ?? false;
  if (istJson) {
    try {
      const huelle = (await antwort.json()) as FehlerHuelle;
      if (huelle.error?.message) {
        return new KontorError(huelle.error.code ?? "internal", huelle.error.message);
      }
    } catch {
      // Fall through to the status-code message below.
    }
  }

  return new KontorError(
    "internal",
    `Der Ereignisstrom des Assistenten antwortete mit Status ${antwort.status}.`,
  );
}
